import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { ContentRefKind } from '@gitbook/app-content-refs';
import { useHiveClient } from '@gitbook/hive-react';
import { PalettePositionWidth, typeQueryInPalette, usePaletteHandle } from '@gitbook/react-uikit';
import {
    Description,
    Label,
    renderIcon,
    TextInput,
    type TextInputProps,
} from '@gitbook/spine-react';
import { normalizeUrlLikeStringToUrl } from '@gitbook/util-url';

import { useContentReferenceContext } from './context';
import { createInputContentRefPalette } from './createInputContentRefPalette';
import { useContentReference } from './useContentReference';

/**
 * Input to edit a content reference.
 */
type ContentReferenceInputFeedback = TextInputProps['feedback'] | { type: 'none' };

export function ContentReferenceInput(props: {
    label?: React.ReactNode;
    labelDescription?: React.ReactNode;
    size?: TextInputProps['size'];
    value: api.ContentRef | null | undefined;
    placeholder?: string;
    onChangeValue: (ref: api.ContentRef | null) => void;
    /** List of accepted references */
    accept?: ContentRefKind[];
    disabled?: boolean;
    feedback?: ContentReferenceInputFeedback;
    clearable?: boolean;
    autoFocus?: boolean;
}): React.ReactElement {
    const {
        label,
        labelDescription,
        value,
        onChangeValue,
        placeholder,
        accept,
        size = 'large',
        disabled = false,
        feedback,
        clearable = true,
        autoFocus = false,
    } = props;

    const inputRef = React.useRef<HTMLInputElement>(null);

    const [focused, setFocused] = React.useState(autoFocus);
    const [dirty, setDirty] = React.useState(false);

    // Set query's default value to the resolved reference anchor if there is one
    const resolvedReference = useContentReference(value);
    const referenceInputValue = !value
        ? ''
        : value.kind === 'anchor' || value.kind === 'url'
          ? resolvedReference.href
          : resolvedReference.title;
    const [query, setQuery] = React.useState(referenceInputValue);

    React.useEffect(() => {
        if (dirty) {
            return;
        }

        setQuery(referenceInputValue);
    }, [dirty, referenceInputValue]);

    // Update palette results as user types in or focuses the input
    const paletteRef = React.useRef<HTMLDivElement>(null);
    const paletteHandle = usePaletteHandle(paletteRef);
    const hiveClient = useHiveClient();
    const contentReferenceContext = useContentReferenceContext();
    React.useEffect(() => {
        // Close the palette if input looses focus
        if (!focused) {
            paletteHandle.close();
            return;
        }

        const paletteQuery = dirty ? query : '';

        if (!paletteHandle.opened) {
            paletteHandle.open(
                typeQueryInPalette(
                    createInputContentRefPalette(
                        (item) => {
                            setDirty(false);
                            onChangeValue(item);
                            setFocused(false);
                            setQuery('');
                            if (inputRef.current) {
                                inputRef.current.blur();
                            }
                        },
                        contentReferenceContext,
                        hiveClient,
                        {
                            search: { enabled: false },
                            position: {
                                width: PalettePositionWidth.ANCHOR,
                            },
                            accept:
                                query.startsWith('http') || query.startsWith('www')
                                    ? ['url']
                                    : accept,
                        }
                    ),
                    paletteQuery
                )
            );
        } else {
            paletteHandle.typeQuery(paletteQuery);
        }
    }, [
        query,
        dirty,
        focused,
        accept,
        contentReferenceContext,
        hiveClient,
        paletteHandle,
        onChangeValue,
    ]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setQuery(newValue);

        if (!newValue) {
            setDirty(false);
            onChangeValue(null);
            return;
        }

        setDirty(true);
    };

    const handleInputBlur = () => {
        if (paletteHandle.opened) {
            // If user modified the query and blurred without changing the selected value
            // we reset the input's value to the selected value.
            if (dirty && query !== referenceInputValue) {
                setDirty(false);
                setQuery(referenceInputValue);
            }

            return;
        }

        // If we cleared the input, also clear the selection
        if (!query) {
            setDirty(false);
            onChangeValue(null);
        }

        // If user enters a valid URL in the query then blurs the palette,
        // consider it an update.
        if (dirty && query) {
            const parsedHref = normalizeUrlLikeStringToUrl(query);

            if (parsedHref) {
                setDirty(false);
                onChangeValue({ kind: 'url', url: parsedHref });
            }
        }

        setFocused(false);
    };

    const handleClear = React.useCallback(() => {
        setQuery('');
        setDirty(false);
        onChangeValue(null);
        setFocused(true);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [onChangeValue]);

    return (
        <div>
            {label ? (
                <div className="mb-2">
                    <Label>{label}</Label>
                    {labelDescription ? (
                        <Description className="mt-1">{labelDescription}</Description>
                    ) : null}
                </div>
            ) : null}

            <div ref={paletteRef}>
                <TextInput
                    data-testid="contentRef.input"
                    ref={inputRef}
                    size={size}
                    value={query}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onFocus={() => setFocused(true)}
                    placeholder={placeholder}
                    disabled={disabled}
                    feedback={feedback?.type === 'none' ? undefined : feedback}
                    autoFocus={autoFocus}
                    onClear={clearable ? handleClear : undefined}
                    prefix={
                        value && !dirty && resolvedReference.icon ? (
                            <span className="flex items-center text-secondary">
                                {renderIcon(resolvedReference.icon, {
                                    size: 16,
                                    className: 'text-secondary',
                                })}
                            </span>
                        ) : undefined
                    }
                />
            </div>
        </div>
    );
}
